import { useState } from "react";
import styles from "./ClientPromoPanel.module.scss";
import Button from "../../../../../../components/Button/Button";
import { sendClientEmail } from "../../../../../../api/client";

function ClientPromoPanel({ client, appointments, stats, topServices }) {
  const [sending, setSending] = useState(false);
  const [feedback, setFeedback] = useState({ type: "", message: "" });
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");

  // Fréquence de visite moyenne (en jours) sur les RDV terminés
  const completedAppointments = (appointments || [])
    .filter((a) => a.status === "confirmed")
    .sort(
      (a, b) => new Date(a.appointment_date) - new Date(b.appointment_date),
    );

  let averageFrequencyLabel = "Pas assez de données";
  if (completedAppointments.length >= 2) {
    const first = new Date(completedAppointments[0].appointment_date);
    const last = new Date(
      completedAppointments[completedAppointments.length - 1].appointment_date,
    );
    const totalDays = Math.max(
      1,
      Math.round((last - first) / (1000 * 60 * 60 * 24)),
    );
    const averageDays = Math.round(
      totalDays / (completedAppointments.length - 1),
    );
    averageFrequencyLabel = `1 RDV tous les ${averageDays} jours environ`;
  }

  const topServiceName =
    topServices && topServices.length > 0 ? topServices[0].name : null;

  const suggestedPromo = topServiceName
    ? `Offre -10% sur "${topServiceName}" pour faire revenir ce client`
    : "Offre de bienvenue pour relancer ce client";

  const buildDefaultEmail = () => {
    const firstName = client?.first_name || "";
    setEmailSubject(`Une offre spéciale pour vous, ${firstName} !`);
    setEmailMessage(
      `Bonjour ${firstName},\n\n` +
        `Cela fait un moment que nous n'avons pas eu le plaisir de vous accueillir.\n` +
        `Pour vous remercier de votre fidélité, nous vous offrons -10% sur votre prochaine prestation` +
        (topServiceName ? ` "${topServiceName}"` : "") +
        `.\n\n` +
        `Cette offre est valable jusqu'au [date à définir].\n\n` +
        `À très vite !`,
    );
    setShowEmailForm(true);
    setFeedback({ type: "", message: "" });
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim() || !emailMessage.trim()) {
      setFeedback({
        type: "error",
        message: "Le sujet et le message sont requis.",
      });
      return;
    }

    setSending(true);
    setFeedback({ type: "", message: "" });

    try {
      await sendClientEmail(client.id, {
        subject: emailSubject,
        message: emailMessage,
      });
      setFeedback({ type: "success", message: "Email envoyé avec succès." });

      // Laisse le message de succès visible 1,5s avant de fermer
      // le formulaire, au lieu de le fermer immédiatement.
      setTimeout(() => {
        setShowEmailForm(false);
      }, 1500);
    } catch (error) {
      console.error("Erreur lors de l'envoi de l'email :", error);
      setFeedback({
        type: "error",
        message:
          error.response?.data?.message ||
          "Une erreur est survenue lors de l'envoi de l'email.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={styles.promoPanel}>
      <h3 className={styles.sectionTitle}>Analyse et opportunités</h3>

      <div className={styles.analysisGrid}>
        <div className={styles.analysisBlock}>
          <h4>Fréquence des rdv</h4>
          <p>{averageFrequencyLabel}</p>
        </div>

        <div className={styles.analysisBlock}>
          <h4>Prestations les plus commandées</h4>
          {topServices && topServices.length > 0 ? (
            <ul className={styles.serviceList}>
              {topServices.map((service) => (
                <li key={service.id}>
                  {service.name}{" "}
                  <span className={styles.serviceCount}>
                    ({service.order_count}x)
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p>Pas encore de données suffisantes.</p>
          )}
        </div>
      </div>

      <div className={styles.promoSuggestion}>
        <h4>Promotion personnalisée suggérée</h4>
        <p className={styles.promoText}>{suggestedPromo}</p>

        {!showEmailForm && (
          <Button
            color="var(--secondary-color)"
            onClick={buildDefaultEmail}
            style={{ padding: "7px 15px", fontSize: "0.85rem" }}
          >
            Préparer l'email
          </Button>
        )}

        {showEmailForm && (
          <div className={styles.emailForm}>
            <div className={styles.formField}>
              <label>Sujet</label>
              <input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className={styles.input}
              />
            </div>

            <div className={styles.formField}>
              <label>Message</label>
              <textarea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                rows={8}
                className={styles.textarea}
              />
            </div>

            {feedback.message && (
              <div
                className={
                  feedback.type === "error"
                    ? styles.errorMessage
                    : styles.successMessage
                }
              >
                {feedback.message}
              </div>
            )}

            <div className={styles.formActions}>
              <Button
                color="var(--secondary-color)"
                onClick={handleSendEmail}
                disabled={sending}
                style={{ padding: "7px 15px", fontSize: "0.85rem" }}
              >
                {sending ? "Envoi..." : "Envoyer par email"}
              </Button>
              <Button
                type="button"
                color="var(--secondary-color)"
                onClick={() => setShowEmailForm(false)}
                style={{ padding: "7px 15px", fontSize: "0.85rem" }}
              >
                Annuler
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientPromoPanel;
